import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { OrganizationMembership, SaraaOrgRole } from '../db/SaraaUser.js';
import { resolveReadOrganizationMemberships } from '../server/resolvers/resolveReadOrganizationMemberships.js';
import { resolveRemoveOrganizationMembership } from '../server/resolvers/resolveRemoveOrganizationMembership.js';
import { resolveSetOrganizationMembership } from '../server/resolvers/resolveSetOrganizationMembership.js';

/**
 * Membership changes go through these rather than the generic entity update.
 *
 * The generic path writes the whole `memberships` array by index, which makes two
 * problems unavoidable: a positional write can be redirected onto a different
 * element by a concurrent reorder, and a save that is a legitimate no-op against
 * the state it was checked on will still re-write every slot — resurrecting a
 * membership that was revoked in between, in an organization the caller has no
 * authority over.
 *
 * These mutations address a membership by `organizationId` instead, so a caller
 * only ever writes the one organization they were authorized for and never
 * touches the others, whatever else is happening concurrently.
 */
const setOrganizationMembership = registerMutation({
  operationName: 'setOrganizationMembership',
  type: () => Boolean,
  params: [
    { name: 'userId', type: () => ID },
    { name: 'organizationId', type: () => ID },
    { name: 'role', type: () => SaraaOrgRole },
  ],
  resolve: resolveSetOrganizationMembership,
});

const removeOrganizationMembership = registerMutation({
  operationName: 'removeOrganizationMembership',
  type: () => Boolean,
  params: [
    { name: 'userId', type: () => ID },
    { name: 'organizationId', type: () => ID },
  ],
  resolve: resolveRemoveOrganizationMembership,
});

const readOrganizationMemberships = registerQuery({
  operationName: 'readOrganizationMemberships',
  type: () => [OrganizationMembership],
  params: [{ name: 'userId', type: () => ID }],
  resolve: resolveReadOrganizationMemberships,
});

export const MembershipAPI = {
  set: setOrganizationMembership,
  remove: removeOrganizationMembership,
  read: readOrganizationMemberships,
};
